import type { TestDriverType } from "@memorang/constants/config/schema";
import type { OriginalItemTypes } from "@memorang/types";
import useSessionStore from "../hooks/useSessionStore";
import useSummativeAssessmentStore from "../hooks/useSummativeAssessmentStore";
import {
	hasCapturedFeedbackResponse,
	resolveFeedbackUserResponse,
	serializeFeedbackUserResponse,
} from "./pte-feedback-response";
import { findSessionItemById } from "./session";
import {
	shouldUseSummativeAiAssessment,
	shouldUseSummativeDynamicFeedback,
} from "./summative-ai-gate";
import {
	type RunAssessmentCtx,
	runAssessment,
} from "./summative-assessment-runner";
import { runFeedback } from "./summative-feedback-runner";

export type SummativeAssessmentGateInputs = {
	testDriver: TestDriverType | undefined;
	isSummativeTest: boolean | undefined;
	itemType: OriginalItemTypes | undefined;
};

export type EnqueueAssessmentCtx = {
	userId: string | undefined;
	projectId: number;
	organizationId: number;
};

const enqueueFeedbackOnLeave = (
	itemId: number,
	runCtx: RunAssessmentCtx,
	gateInputs: SummativeAssessmentGateInputs,
): boolean => {
	if (
		!shouldUseSummativeDynamicFeedback(
			gateInputs.testDriver,
			gateInputs.isSummativeTest,
			gateInputs.itemType,
		)
	) {
		return false;
	}
	const item = findSessionItemById(
		useSessionStore.getState().sessionItems,
		itemId,
	);
	if (!(item && hasCapturedFeedbackResponse(item))) {
		return false;
	}
	const responseHash = serializeFeedbackUserResponse(
		resolveFeedbackUserResponse(item),
	);
	if (
		!useSummativeAssessmentStore
			.getState()
			.shouldRunFeedback(itemId, responseHash)
	) {
		return false;
	}
	// Fire-and-forget: never awaited, so navigation isn't blocked on grading.
	runFeedback(itemId, runCtx);
	return true;
};

export const enqueueAssessmentOnLeave = (
	itemId: number | undefined,
	ctx: EnqueueAssessmentCtx,
	gateInputs: SummativeAssessmentGateInputs,
): boolean => {
	if (!(itemId && ctx.userId)) {
		return false;
	}
	const runCtx: RunAssessmentCtx = {
		userId: ctx.userId,
		projectId: ctx.projectId,
		organizationId: ctx.organizationId,
	};

	if (
		shouldUseSummativeAiAssessment(
			gateInputs.testDriver,
			gateInputs.isSummativeTest,
			gateInputs.itemType,
		)
	) {
		if (!useSummativeAssessmentStore.getState().shouldRunAssessment(itemId)) {
			return false;
		}
		// Fire-and-forget: never awaited, so navigation isn't blocked on grading.
		runAssessment(itemId, runCtx);
		return true;
	}

	return enqueueFeedbackOnLeave(itemId, runCtx, gateInputs);
};

export const markResponseChangedIfGated = (
	itemId: number,
	gateInputs: SummativeAssessmentGateInputs,
): boolean => {
	if (
		!shouldUseSummativeAiAssessment(
			gateInputs.testDriver,
			gateInputs.isSummativeTest,
			gateInputs.itemType,
		)
	) {
		return false;
	}
	useSummativeAssessmentStore.getState().markResponseChanged(itemId);
	return true;
};
